import React from 'react';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { ASCENDING, DESCENDING } from 'constants/sortDirections';
import { noop } from 'utils/noop';
import { getCellWidth } from './helpers';
import ActionCell from './ActionCell';
import * as t from './cellTypes';
import {
  HeaderCell as StyledHeaderCell,
  HeaderCellButton,
  HeaderCellContainer,
  StyledSemanticCheckbox,
} from './HeaderCell.styled';

// type Props = {
//   actionCell: any,
//   /** Text align of cell. */
//   align: string,
//   /** If header cell is checked. */
//   checked: boolean,
//   /** An additional table cell class. */
//   className: string,
//   /** @private */
//   docked: boolean,
//   /** Custom header function to return contents of custom header cell if using custom headers */
//   getHeaderCell: (props: {}) => *,
//   /** If cell is check box and checked state is indeterminate. */
//   indeterminate: boolean,
//   /** Max width of column. (Pixels) */
//   maxWidth: number | string,
//   /** Min width of column. (Pixels) */
//   minWidth: number | string,
//   /** Name attribute of the header cell. */
//   name: string,
//   /**
//    * Function to run when sort is changed.
//    *
//    * ```
//    * onChangeSort(e, sortProps)
//    * ```
//    * @param { Object } e: The change event.
//    * @param { Object } sortProps: The props of the header cell containing the updated sort.
//    */
//   onChangeSort: (e: {}, sortProps: {}) => {},
//   /**
//    * Function to run when header cell is clicked.
//    *
//    * ```
//    * onClick(e, props)
//    * ```
//    * @param { Object } e: The click event.
//    * @param { Object } props: The props of the header cell.
//    */
//   onClick: (e: {}, props: {}) => {},
//   /** Current sort column. */
//   orderBy: string,
//   /** If header spans entire table. */
//   shrink: boolean,
//   /** If the header cell is sortable. */
//   sortable: boolean,
//   /** Current sort direction. */
//   sorting: string,
//   /** The cell type. */
//   type: string,
//   /** The value of the header cell. */
//   value: string,
//   /** Width of the cell. Overrides minWidth and maxWidth. */
//   width: number,
// };

export default class HeaderCell extends React.Component {
  static defaultProps = {
    align: 'left',
    onChangeSort: noop,
  };

  /**
   * Callback function when a sorable header id clicked.
   * @param { Object } e: The click event.
   * @param { Object } headerProps: The header props including the updated sort value.
   * @returns { void }
   */
  handleChangeSort = (e, { value }) => {
    const { onChangeSort, orderBy, sorting } = this.props;
    if (value === orderBy && sorting === DESCENDING) {
      onChangeSort(e, { orderBy: value, sorting: ASCENDING });
    } else {
      onChangeSort(e, { orderBy: value, sorting: DESCENDING });
    }
  };

  /**
   * Generates a checkbox header cell.
   * @returns { React.Component } The generated checkbox cell.
   */
  getCheckBoxCell = () => {
    const { checked, indeterminate, onClick, value } = this.props;
    return (
      <StyledSemanticCheckbox
        data-test="header-cell-checkbox"
        checked={checked}
        indeterminate={indeterminate}
        onChange={onClick}
        value={value}
      />
    );
  };

  /**
   * Generates a text header cell.
   * @returns { React.Component } The generated text cell.
   */
  getTextCell = () => {
    const { align, name, orderBy, sorting, sortable, value } = this.props;
    const style = { textAlign: align };
    let sortIcon = 'sort';
    if (orderBy === value) {
      switch (sorting) {
        case ASCENDING:
          sortIcon = 'sort up';
          break;
        default:
          sortIcon = 'sort down';
      }
    }

    if (sortable) {
      const onClick = (e) => this.handleChangeSort(e, { value });
      return (
        <HeaderCellButton onClick={onClick} data-test="header-cell-sortable">
          {this.getActionCell()}
          {name}
          <SemanticUI.Icon name={sortIcon} />
        </HeaderCellButton>
      );
    }
    return (
      <StyledHeaderCell style={style}>
        {this.getActionCell()}
        {name}
      </StyledHeaderCell>
    );
  };

  /**
   * getCellContent
   * Generates the contents of a header cell.
   * @returns { React.Component } The generated cell.
   */
  getCellContent = () => {
    const { type, getHeaderCell } = this.props;
    switch (type) {
      case t.CHECKBOX:
        return this.getCheckBoxCell();
      case t.CUSTOMHEADER:
        return <StyledHeaderCell>{getHeaderCell(this.props)}</StyledHeaderCell>;
      default:
        return this.getTextCell();
    }
  };

  /**
   * getActionCell
   * Generates the action cell
   * @returns { React.Component } The action cell.
   */
  getActionCell = () => {
    const { actionCell } = this.props;
    if (actionCell) {
      return <ActionCell {...actionCell} contents={actionCell.header} hideActions />;
    }
    return undefined;
  };

  render() {
    const {
      actionCell,
      className,
      docked,
      shrink,
      maxWidth,
      minWidth,
      orderBy,
      sortable,
      type,
      value,
      width,
    } = this.props;
    return (
      <HeaderCellContainer
        className={className}
        data-active-sort={orderBy === value || undefined}
        data-actions={!!actionCell || undefined}
        data-docked={docked}
        data-group={(!actionCell && type === t.GROUP) || undefined}
        data-role="header-cell"
        data-shrink={shrink}
        data-sortable={sortable}
        data-checkbox={type === t.CHECKBOX}
        style={{ ...getCellWidth(width, minWidth, maxWidth) }}
      >
        {this.getCellContent()}
      </HeaderCellContainer>
    );
  }
}
